import { useHistory } from 'react-router-dom';

import { useAbeetContext } from '@clutter/abeet';

import { DraftFragment, Lead__ServiceNeeds } from '@graphql/platform';

import { buildCheckoutQueryString } from '@root/components/checkout/utilities/checkout_url';
import { generateLeadSource } from '@root/resources/lead';
import { WWW_ROUTES } from '@root/root/routes';

import { Action, useClientDataContext } from '@shared/client_data_context';

import { getPageName } from '@utils/get_pagename_utilities';
import { enrollStorageBookingFunnelTest } from '@utils/hooks/storage_booking_funnel_test';

const FUNNEL_SERVICES = [
  Lead__ServiceNeeds.SmartStorage,
  Lead__ServiceNeeds.Moving,
] as const;

export const hasHomeDraft = (draft?: DraftFragment | null) =>
  !!draft?.latestService &&
  FUNNEL_SERVICES.includes(
    draft.latestService as (typeof FUNNEL_SERVICES)[number],
  );

const getDraftResumePath = (draft: DraftFragment) => {
  const zip =
    (draft.latestService === Lead__ServiceNeeds.Moving
      ? draft.movingOriginAttributes?.zip
      : draft.address?.zip) ?? undefined;

  let path: string =
    draft.latestService === Lead__ServiceNeeds.Moving
      ? WWW_ROUTES.PRODUCT_PAGE_MOVING
      : WWW_ROUTES.PRODUCT_PAGE_STORAGE;

  if (draft.latestService === Lead__ServiceNeeds.SmartStorage && zip) {
    path = `${path}?${buildCheckoutQueryString({ preserveSearch: '', zip, zipValidated: true })}`;
  }

  return { path, zip };
};

export const useDraftResume = (
  draft?: DraftFragment | null,
  entryPoint = 'home',
) => {
  const { dispatch } = useClientDataContext();
  const history = useHistory();
  const { execute } = useAbeetContext();
  const { path, zip } = draft
    ? getDraftResumePath(draft)
    : { path: '', zip: undefined };

  const resume = async (event?: { preventDefault(): void }) => {
    if (!draft) return;

    event?.preventDefault();
    dispatch({
      type: Action.SetupCheckout,
      payload: {
        leadSource: generateLeadSource({
          domain: 'www',
          pageName: getPageName(),
          entryPoint,
        }),
        zip,
        draft,
      },
    });

    if (draft.latestService === Lead__ServiceNeeds.SmartStorage) {
      await enrollStorageBookingFunnelTest(execute);
    }

    history.push(path);
  };

  return { path, resume };
};
